import DetailsPageTitle from "components/common/DetailsPageTitle";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import CustomLabel from "components/common/CustomLabel";
import {
  getEditorStyle,
  toolbarOptions,
  wrapperStyle,
} from "components/constants/editorOptions";
import {
  getPageContentByType,
  updatePageContentByType,
} from "store/actions/globalSettingSlice";
import draftToHtml from "draftjs-to-html";
import { showAlert } from "store/reducers/alertReducer";
import Spinners from "components/common/Spinners";
import htmlToDraft from "html-to-draftjs";

const PolicyPage = ({ policyType, pageTitle }) => {
  const { t } = useTranslation();
  document.title = t(`${pageTitle} | Work Space`);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, loading, pageContent } = useSelector(
    (state) => state.GlobalSettings
  );

  // Editor states
  const [editorEn, setEditorEn] = useState(EditorState.createEmpty());
  const [editorAr, setEditorAr] = useState(EditorState.createEmpty());

  const [formValues, setFormValues] = useState({
    description: "",
    descriptionAr: "",
  });

  useEffect(() => {
    const getContent = () => {
      const payload = {
        type: policyType,
      };
      dispatch(getPageContentByType(payload));
    };
    getContent();
  }, [policyType]);

  useEffect(() => {
    if (pageContent) {
      const { description, descriptionAr } = pageContent || {};
      setFormValues({
        description,
        descriptionAr,
      });

      // Convert English HTML to EditorState
      if (description) {
        const contentBlock = htmlToDraft(description);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          const editorState = EditorState.createWithContent(contentState);
          setEditorEn(editorState);
        }
      }

      // Convert Arabic HTML to EditorState
      if (descriptionAr) {
        const contentBlockAr = htmlToDraft(descriptionAr);
        if (contentBlockAr) {
          const contentStateAr = ContentState.createFromBlockArray(
            contentBlockAr.contentBlocks
          );
          const editorStateAr = EditorState.createWithContent(contentStateAr);
          setEditorAr(editorStateAr);
        }
      }
    }
  }, [pageContent]);

  const handleEditorChange = (lang, editorState) => {
    if (lang === "en") setEditorEn(editorState);
    else if (lang === "ar") setEditorAr(editorState);

    const rawContent = convertToRaw(editorState.getCurrentContent());
    const html = draftToHtml(rawContent);

    setFormValues((prev) => ({
      ...prev,
      [lang === "en" ? "description" : "descriptionAr"]: html,
    }));
  };

  const handleSave = () => {
    const { description, descriptionAr } = formValues || {};
    // Check if descriptions are empty
    if (
      !description ||
      description === "<p></p>" ||
      description === "<p></p>\n"
    ) {
      dispatch(
        showAlert({ text: t("English description is required"), type: "error" })
      );
      return;
    }

    if (
      !descriptionAr ||
      descriptionAr === "<p></p>" ||
      descriptionAr === "<p></p>\n"
    ) {
      dispatch(
        showAlert({ text: t("Arabic description is required"), type: "error" })
      );
      return;
    }

    const { title, titleAr } = pageContent || {};
    const payload = {
      type: policyType,
      title,
      titleAr,
      description,
      descriptionAr,
    };

    dispatch(updatePageContentByType(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          navigate(-1)
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t(pageTitle)} />
              <div className="common_wrapper">
                <Row>
                  {/* English Editor */}
                  <Col lg={6}>
                    <div className="form_field mb-1">
                      <CustomLabel
                        htmlFor="description"
                        label={t("English Description")}
                      />
                    </div>
                    <Editor
                      editorState={editorEn}
                      onEditorStateChange={(state) =>
                        handleEditorChange("en", state)
                      }
                      wrapperStyle={wrapperStyle}
                      editorStyle={getEditorStyle("en")}
                      toolbar={toolbarOptions}
                      placeholder={t("Enter English Description")}
                    />
                  </Col>

                  {/* Arabic Editor */}
                  <Col lg={6}>
                    <div className="form_field mb-1">
                      <CustomLabel
                        htmlFor="descriptionAr"
                        label={t("Arabic Description")}
                      />
                    </div>
                    <Editor
                      editorState={editorAr}
                      onEditorStateChange={(state) =>
                        handleEditorChange("ar", state)
                      }
                      wrapperStyle={wrapperStyle}
                      editorStyle={getEditorStyle("sa")}
                      toolbar={toolbarOptions}
                      placeholder={t("Enter Arabic Description")}
                    />
                  </Col>
                </Row>
              </div>
              <Row>
                <Col lg={12}>
                  <div className="d-block ms-auto" style={{ maxWidth: 315 }}>
                    <div className="g_end  gap-3">
                      <button
                        type="button"
                        className="cancel_btn w-50"
                        onClick={() => navigate(-1)}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="main_btn w-50"
                        disabled={isLoading}
                        onClick={handleSave}
                      >
                        {t("Save")}
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default PolicyPage;
